// Parser CTe para o frontend (src/utils/parseCTe.js)
// Versão corrigida com encoding correto

import { classifyNotaFinanceira, financialClassificationUtils } from './financialClassification';

export function parseCTe(xmlDoc) {
    console.log('Iniciando parse do CTe no frontend...');
    
    try {
      // Busca o nó principal do CTe
      let cteNode = xmlDoc.getElementsByTagName('CTe')[0];
      if (!cteNode) {
        cteNode = xmlDoc.getElementsByTagName('cteProc')[0]?.getElementsByTagName('CTe')[0];
      }
      
      if (!cteNode) {
        console.error('Nó CTe não encontrado no XML');
        throw new Error('Não foi possível encontrar o nó CTe no XML');
      }
  
      // Função auxiliar para extrair texto
      const getNodeValue = (parent, tagName) => {
        if (!parent) return '';
        const node = parent.getElementsByTagName(tagName)[0];
        return node?.textContent?.trim() || '';
      };
  
      // Função para formatar data
      const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const datePart = dateStr.split('T')[0];
        return datePart;
      };
  
      // 1. Informações básicas do CTe
      const ideNode = cteNode.getElementsByTagName('ide')[0];
      const nCT = getNodeValue(ideNode, 'nCT');
      const serie = getNodeValue(ideNode, 'serie');
      const dhEmi = getNodeValue(ideNode, 'dhEmi');
      const CFOP = getNodeValue(ideNode, 'CFOP');
      const natOp = getNodeValue(ideNode, 'natOp');
  
      // 2. Chave de acesso
      const infCteNode = cteNode.getElementsByTagName('infCte')[0];
      const chaveAcesso = infCteNode?.getAttribute('Id')?.replace('CTe', '') || '';
  
      // 3. Emitente (Transportadora)
      const emitNode = cteNode.getElementsByTagName('emit')[0];
      const emitCNPJ = getNodeValue(emitNode, 'CNPJ');
      const emitIE = getNodeValue(emitNode, 'IE');
      const emitNome = getNodeValue(emitNode, 'xNome');
      const emitFantasia = getNodeValue(emitNode, 'xFant');
  
      // 4. Remetente
      const remNode = cteNode.getElementsByTagName('rem')[0];
      const remCNPJ = getNodeValue(remNode, 'CNPJ');
      const remNome = getNodeValue(remNode, 'xNome');
      
      // 5. Destinatário
      const destNode = cteNode.getElementsByTagName('dest')[0];
      const destCNPJ = getNodeValue(destNode, 'CNPJ');
      const destNome = getNodeValue(destNode, 'xNome');
  
      // 6. Valores da Prestação
      const vPrestNode = cteNode.getElementsByTagName('vPrest')[0];
      const vTPrest = parseFloat(getNodeValue(vPrestNode, 'vTPrest') || 0);
      const vRec = parseFloat(getNodeValue(vPrestNode, 'vRec') || 0);
      
      // Componentes do valor
      const componentes = [];
      const compNodes = vPrestNode?.getElementsByTagName('Comp') || [];
      for (let comp of compNodes) {
        componentes.push({
          nome: getNodeValue(comp, 'xNome'),
          valor: parseFloat(getNodeValue(comp, 'vComp') || 0)
        });
      }
  
      // 7. Informações da Carga
      const infCargaNode = cteNode.getElementsByTagName('infCarga')[0];
      const vCarga = parseFloat(getNodeValue(infCargaNode, 'vCarga') || 0);
      const proPred = getNodeValue(infCargaNode, 'proPred');
      
      // Peso da carga
      const infQNodes = infCargaNode?.getElementsByTagName('infQ') || [];
      let peso = 0;
      for (let infQ of infQNodes) {
        const tpMed = getNodeValue(infQ, 'tpMed');
        if (tpMed.includes('PESO')) {
          peso = parseFloat(getNodeValue(infQ, 'qCarga') || 0);
          break;
        }
      }
  
      // 8. Documentos Relacionados (NFe)
      const infDocNode = cteNode.getElementsByTagName('infDoc')[0];
      const infNFeNode = infDocNode?.getElementsByTagName('infNFe')[0];
      const chaveNFe = getNodeValue(infNFeNode, 'chave');
      const dPrev = getNodeValue(infNFeNode, 'dPrev');
  
      // 9. Data de vencimento sugerida
      const dataVencimento = dPrev || formatDate(dhEmi);
  
      console.log('Dados extraídos do CTe:', {
        numero: nCT,
        serie,
        transportadora: emitNome,
        valor: vTPrest
      });

      const classificacaoFinanceira = classifyNotaFinanceira({
        cfop: CFOP,
        naturezaOperacao: natOp,
        valorTotal: vTPrest,
        documento: 'cte',
      });

      const cfopNormalizado =
        classificacaoFinanceira.cfopNormalizado || financialClassificationUtils.normalizeCfop(CFOP);
      const cfopFormatado =
        classificacaoFinanceira.cfopFormatado || financialClassificationUtils.formatCfop(cfopNormalizado || CFOP);
  
      // Montar objeto compatível com o sistema
      const dadosCTe = {
        // Identificação do documento
        tipo_documento: 'CTe',
        numero: nCT,
        serie: serie,
        chave_acesso: chaveAcesso,
        data_emissao: formatDate(dhEmi),
        
        // Tipo de operação (CTe é sempre despesa/frete a pagar)
        tipo: 'compra',
        tipo_operacao: 'pagar',
        
        // CFOP e natureza
        cfop: cfopFormatado || cfopNormalizado || '',
        cfop_codigo: cfopNormalizado || '',
        cfop_formatado: cfopFormatado || '',
        natureza_operacao: natOp,
        
        // Transportadora (emitente no CTe)
        fornecedor_cliente: emitNome || emitFantasia,
        cnpj_cpf: emitCNPJ,
        inscricao_estadual: emitIE,
        
        // Dados específicos do CTe
        transportadora: {
          cnpj: emitCNPJ,
          ie: emitIE,
          nome: emitNome,
          fantasia: emitFantasia
        },
        
        remetente: {
          cnpj: remCNPJ,
          nome: remNome
        },
        
        destinatario: {
          cnpj: destCNPJ,
          nome: destNome
        },
        
        // Valores
        valor_total: vTPrest.toFixed(2),
        valor_receber: vRec.toFixed(2),
        valor_carga: vCarga.toFixed(2),
        
        // Componentes de valor (para exibir no detalhamento)
        componentes_valor: componentes,
        
        // Carga
        produto: proPred,
        peso: peso,
        
        // NFe relacionada
        chave_nfe_relacionada: chaveNFe,
        
        // Data de vencimento
        data_vencimento: dataVencimento,
        
        // Descrição para o lançamento
        descricao: `Frete CT-e ${nCT}${proPred ? ' - ' + proPred : ''}`,
        
        // Categoria padrão
        categoria: 'Transporte',
        
        // Observações (se houver)
        observacoes: natOp,

        // Classificação financeira
        gera_financeiro: classificacaoFinanceira.geraFinanceiro,
        motivo_financeiro: classificacaoFinanceira.geraFinanceiro
          ? ''
          : classificacaoFinanceira.motivoFinanceiro,
        classificacao_financeira: {
          ...classificacaoFinanceira,
          cfopNormalizado: cfopNormalizado || '',
          cfopFormatado: cfopFormatado || '',
        },
        
        // Identificação visual
        icone_tipo: '🚚',
        badge_tipo: 'CT-e',
        cor_badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
        
        // Flag para identificar origem
        origem: 'xml_cte'
      };
  
      console.log('CTe parseado com sucesso:', dadosCTe);
      return dadosCTe;
      
    } catch (error) {
      console.error('Erro ao parsear CTe:', error);
      throw new Error(`Erro ao processar CTe: ${error.message}`);
    }
  }
  
  // Função para identificar se é CTe
  export function isCTe(xmlDoc) {
    const hasCTe = xmlDoc.getElementsByTagName('CTe').length > 0 || 
                   xmlDoc.getElementsByTagName('cteProc').length > 0;
    console.log('É CTe?', hasCTe);
    return hasCTe;
  }
  
  // Função para identificar se é NFe
  export function isNFe(xmlDoc) {
    const hasNFe = xmlDoc.getElementsByTagName('NFe').length > 0 || 
                   xmlDoc.getElementsByTagName('nfeProc').length > 0;
    console.log('É NFe?', hasNFe);
    return hasNFe;
  }